import React from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { SortDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, TableSortLabel } from '@mui/material';
import { SortDirection } from '@tanstack/react-table';
import { isNumberType } from '../Table';

const emptyIcon = () => <Box sx={{ display: 'none' }} />;

interface SortIconProps {
  sortDirection: false | SortDirection;
}

const SortIcon = ({ sortDirection }: SortIconProps) => (
  <Box
    component={SortDown}
    className="sort-icon"
    sx={{
      width: 18,
      height: 18,
      transition: theme =>
        theme.transitions.create(['transform', 'opacity'], {
          duration: theme.transitions.duration.short,
        }),
      opacity: sortDirection ? 1 : 0,
      transform: { asc: 'scaleY(-1)', desc: 'scaleY(1)' }[sortDirection as string] ?? 'scaleY(1)',
    }}
  />
);

interface SortableHeaderCellProps {
  isSorted: false | SortDirection;
  initialSortDirection: SortDirection;
  columnType?: string;
  toggleSortingHandler?: (event: React.MouseEvent<HTMLSpanElement>) => void;
  children: React.ReactNode;
  filterIconButton?: React.ReactNode;
}

const SortableHeaderCell = ({
  isSorted,
  initialSortDirection,
  columnType,
  toggleSortingHandler,
  children,
  filterIconButton,
}: SortableHeaderCellProps) => (
  <TableSortLabel
    active={!!isSorted}
    direction={isSorted || initialSortDirection}
    IconComponent={emptyIcon}
    onClick={e => {
      const target = e.target as HTMLElement;
      if (target.closest('.sort-icon-box') || target.closest('.sort-title')) {
        toggleSortingHandler?.(e);
      }
    }}
    sx={{
      width: '100%',
      ...(isNumberType(columnType) ? { flexDirection: 'row-reverse' } : {}),
      ...flex.itemsCenter,
      gap: 0.5,
      cursor: 'default',
    }}>
    <Box sx={{ ...ellipsis, cursor: 'pointer' }} className="sort-title">
      <AvTooltip>{children}</AvTooltip>
    </Box>
    <Box
      sx={{
        cursor: 'pointer',
      }}
      className="sort-icon-box">
      <SortIcon sortDirection={isSorted} />
    </Box>
    {filterIconButton}
  </TableSortLabel>
);

export default SortableHeaderCell;
